import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { Product, Category, ProductFormData } from '../types/types';
import { Package, Plus, Edit2, Trash2, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import './ProductManagement.css';

const ProductManagement: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [formData, setFormData] = useState<ProductFormData>({
        name: '',
        description: '',
        barcode: '',
        imageUrl: '',
        price: 0,
        cost: 0,
        stock: 0,
        minStockThreshold: 0,
        isActive: true,
        categoryId: 1,
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [productsData, categoriesData] = await Promise.all([
                productService.getAllProducts(),
                categoryService.getAllCategories(),
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
            if (categoriesData.length > 0) {
                setFormData((prev) => ({ ...prev, categoryId: categoriesData[0].id }));
            }
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setFormData({
            name: '',
            description: '',
            barcode: '',
            imageUrl: '',
            price: 0,
            cost: 0,
            stock: 0,
            minStockThreshold: 0,
            isActive: true,
            categoryId: categories[0]?.id || 1,
        });
        setCreateModalOpen(true);
    };

    const handleEdit = (product: Product) => {
        setSelectedProduct(product);
        setFormData({
            name: product.name,
            description: product.description || '',
            barcode: product.barcode,
            imageUrl: product.imageUrl || '',
            price: product.price,
            cost: product.cost,
            stock: product.stock,
            minStockThreshold: product.minStockThreshold || 0,
            isActive: product.isActive,
            categoryId: product.categoryId,
        });
        setEditModalOpen(true);
    };

    const handleDelete = (product: Product) => {
        setSelectedProduct(product);
        setDeleteModalOpen(true);
    };

    const confirmCreate = async () => {
        try {
            await productService.createProduct(formData);
            await loadData();
            setCreateModalOpen(false);
        } catch (error) {
            console.error('Error creating product:', error);
            alert('Error al crear el producto');
        }
    };

    const confirmEdit = async () => {
        if (!selectedProduct) return;

        try {
            await productService.updateProduct(selectedProduct.id, {
                ...selectedProduct,
                ...formData,
            });
            await loadData();
            setEditModalOpen(false);
            setSelectedProduct(null);
        } catch (error) {
            console.error('Error updating product:', error);
            alert('Error al actualizar el producto');
        }
    };

    const confirmDelete = async () => {
        if (!selectedProduct) return;

        try {
            await productService.deleteProduct(selectedProduct.id);
            await loadData();
            setDeleteModalOpen(false);
            setSelectedProduct(null);
        } catch (error: any) {
            console.error('Error deleting product:', error);
            const message = error.response?.data?.message || 'Error al eliminar el producto';
            alert(message);
            setDeleteModalOpen(false);
        }
    };

    const toggleActive = async (product: Product) => {
        try {
            await productService.updateProduct(product.id, {
                ...product,
                isActive: !product.isActive,
            });
            await loadData();
        } catch (error) {
            console.error('Error toggling product status:', error);
            alert('Error al cambiar el estado del producto');
        }
    };

    const filteredProducts = products.filter(
        (product) =>
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.category?.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="product-management-container">
            <div className="page-header">
                    <div>
                        <h1>Gestión de Productos</h1>
                        <p className="page-subtitle">Administrar inventario y catálogo de productos</p>
                    </div>
                    <Package size={32} className="header-icon" />
                </div>

                <div className="card">
                    <div className="table-controls">
                        <div className="search-box">
                            <Search size={20} />
                            <input
                                type="text"
                                placeholder="Buscar productos..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <button className="btn btn-primary" onClick={handleCreate}>
                            <Plus size={20} />
                            Nuevo Producto
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-state">Cargando productos...</div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nombre</th>
                                        <th>Categoría</th>
                                        <th>Precio</th>
                                        <th>Stock</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map((product) => (
                                        <tr key={product.id}>
                                            <td>{product.id}</td>
                                            <td>
                                                <div className="product-cell">
                                                    <div className="product-info-name">
                                                        <span className="product-name">{product.name}</span>
                                                        <span className="product-barcode">{product.barcode}</span>
                                                    </div>
                                                    {product.description && (
                                                        <span className="product-desc">{product.description}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="category-badge">{product.category?.name}</span>
                                            </td>
                                            <td className="price-cell">${product.price.toFixed(2)}</td>
                                            <td>
                                                <span className={`stock-badge ${product.stock <= (product.minStockThreshold || 5) ? 'low-stock' : ''}`}>
                                                    {product.stock} unidades
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    className="status-toggle"
                                                    onClick={() => toggleActive(product)}
                                                    title={product.isActive ? 'Desactivar' : 'Activar'}
                                                >
                                                    {product.isActive ? (
                                                        <>
                                                            <ToggleRight size={24} className="toggle-on" />
                                                            <span className="status-active">Activo</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ToggleLeft size={24} className="toggle-off" />
                                                            <span className="status-inactive">Inactivo</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => handleEdit(product)}
                                                        title="Editar producto"
                                                    >
                                                        <Edit2 size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDelete(product)}
                                                        title="Eliminar producto"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredProducts.length === 0 && (
                                <div className="empty-state">No se encontraron productos</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Create Modal */}
                <Modal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    title="Crear Nuevo Producto"
                    size="medium"
                >
                    <ProductForm
                        formData={formData}
                        setFormData={setFormData}
                        categories={categories}
                        onSubmit={confirmCreate}
                        onCancel={() => setCreateModalOpen(false)}
                    />
                </Modal>

                {/* Edit Modal */}
                <Modal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    title="Editar Producto"
                    size="medium"
                >
                    <ProductForm
                        formData={formData}
                        setFormData={setFormData}
                        categories={categories}
                        onSubmit={confirmEdit}
                        onCancel={() => setEditModalOpen(false)}
                    />
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    title="Confirmar Eliminación"
                    size="small"
                >
                    <div className="delete-confirmation">
                        <p>
                            ¿Está seguro que desea eliminar el producto{' '}
                            <strong>{selectedProduct?.name}</strong>?
                        </p>
                        <p className="warning-text">Esta acción no se puede deshacer.</p>

                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteModalOpen(false)}>
                                Cancelar
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete}>
                                Eliminar
                            </button>
                        </div>
                    </div>
                </Modal>
            </div>
    );
};

// Product Form Component
interface ProductFormProps {
    formData: ProductFormData;
    setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
    categories: Category[];
    onSubmit: () => void;
    onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
    formData,
    setFormData,
    categories,
    onSubmit,
    onCancel,
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData({
            ...formData,
            [name]:
                type === 'number'
                    ? parseFloat(value) || 0
                    : type === 'checkbox'
                        ? (e.target as HTMLInputElement).checked
                        : value,
        });
    };

    return (
        <div className="modal-form">
            <div className="form-group">
                <label htmlFor="name">Nombre del Producto *</label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                />
            </div>

            <div className="form-group">
                <label htmlFor="description">Descripción</label>
                <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={2}
                    className="form-input"
                />
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="barcode">Código de Barras *</label>
                    <input
                        type="text"
                        id="barcode"
                        name="barcode"
                        value={formData.barcode}
                        onChange={handleChange}
                        required
                        className="form-input"
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="imageUrl">URL de Imagen</label>
                    <input
                        type="text"
                        id="imageUrl"
                        name="imageUrl"
                        value={formData.imageUrl}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="https://..."
                    />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label htmlFor="price">Precio *</label>
                    <input
                        type="number"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        required
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="stock">Stock *</label>
                    <input
                        type="number"
                        id="stock"
                        name="stock"
                        value={formData.stock}
                        onChange={handleChange}
                        min="0"
                        required
                        className="form-input"
                    />
                </div>
            </div>

            <div className="form-row">
                <div className='form-group'>
                    <label htmlFor="cost">Costo Producto</label>
                    <input                        type="number"
                        id="cost"
                        name="cost"
                        value={formData.cost}
                        onChange={handleChange}
                        min="0"
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label htmlFor="minStockThreshold">Stock Mínimo (Alerta)</label>
                    <input
                        type="number"
                        id="minStockThreshold"
                        name="minStockThreshold"
                        value={formData.minStockThreshold}
                        onChange={handleChange}
                        min="0"
                        className="form-input"
                    />
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="categoryId">Categoría *</label>
                <select
                    id="categoryId"
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    required
                    className="form-select"
                >
                    {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                            {category.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="form-group">
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                        className="form-checkbox"
                    />
                    <span>Producto activo</span>
                </label>
            </div>

            <div className="modal-actions">
                <button className="btn btn-outline" onClick={onCancel}>
                    Cancelar
                </button>
                <button className="btn btn-primary" onClick={onSubmit}>
                    Guardar
                </button>
            </div>
        </div>
    );
};

export default ProductManagement;
